<template>
  <div class="add-package-wrapper fade-in">
    <!-- ===== Header ===== -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">{{ isEdit ? "Edit Package" : "Create New Package" }}</h2>
        <p class="page-subtitle">
          {{ isEdit ? "Update your package details below." : "Define your service package details below." }}
        </p>
      </div>
      <div class="header-right">
        <button class="btn-help"><i class="bx bx-help-circle"></i> Help</button>
      </div>
    </div>

    <!-- ===== Form Card ===== -->
    <div class="form-card">
      <form class="form-content" @submit.prevent="handleSubmit">
        <div class="form-group">
          <label class="form-label">Package Name</label>
          <input
            v-model="form.package_name"
            type="text"
            class="form-input"
            placeholder="Enter package name"
          />
        </div>

        <div class="form-row">
          <div class="form-group half">
            <label class="form-label">Package Amount ($)</label>
            <input
              v-model="form.package_amount"
              type="number"
              class="form-input"
              placeholder="Enter amount"
            />
          </div>
          <div class="form-group half">
            <label class="form-label">Duration (Minutes)</label>
            <input
              v-model="form.package_duration"
              type="number"
              class="form-input"
              placeholder="Enter duration"
            />
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea
            v-model="form.package_desc"
            class="form-input"
            rows="3"
            placeholder="Describe this package..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Status</label>
          <select v-model="form.package_status" class="form-input">
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div class="form-footer">
          <button type="button" class="btn-secondary" @click="cancelForm">Cancel</button>
          <button type="submit" class="btn-primary">
            {{ isEdit ? "Update Package" : "Create Package" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { createPackage } from "@/apis/pkgapis";

const router = useRouter();
const toast = useToast();

const isEdit = ref(false);
const form = ref({
  package_id: "",
  package_name: "",
  package_amount: "",
  package_duration: "",
  package_desc: "",
  package_status: "Active",
  package_by: "",
});

onMounted(() => {
  try {
    // ✅ Load logged-in user info
    const userData = JSON.parse(sessionStorage.getItem("ldata"));
    form.value.package_by = userData?.user_id || "";

    // ✅ Load package data from session storage
    const storedData = sessionStorage.getItem("package_data");
    if (storedData) {
      const pkg = JSON.parse(storedData);

      // ✅ Map manually stored keys to form fields
      form.value = {
        ...form.value,
        package_id: pkg.id || "",
        package_name: pkg.name || "",
        package_amount: pkg.price || "",
        package_desc: pkg.description || "",
        package_status: pkg.status || "Active",
        package_duration: pkg.duration || "",
      };

      isEdit.value = true;
      toast.success("Editing existing package ✏️", { timeout: 2000 });
    } else {
      toast.success("Creating a new package 🆕", { timeout: 2000 });
    }
  } catch (err) {
    console.error("Error loading package data:", err);
    toast.error("Failed to load package data!");
  }
});

const validateForm = () => {
  if (!form.value.package_name.trim()) {
    toast.error("Please enter a package name");
    return false;
  }
  if (!form.value.package_amount) {
    toast.error("Please enter a package amount");
    return false;
  }
  if (!form.value.package_desc.trim()) {
    toast.error("Please add a description");
    return false;
  }
  return true;
};

const handleSubmit = async () => {
  if (!validateForm()) return;

  try {
    const payload = { ...form.value };
    const res = await createPackage(payload);

    if (res.status) {
      toast.success(res.success || "Package saved successfully!");
      sessionStorage.removeItem("package_data");
      setTimeout(() => router.push("/packages_list"), 1000);
    } else {
      toast.error(res.success || "Failed to save package");
    }
  } catch (err) {
    console.error("Package save error:", err);
    toast.error("Something went wrong while saving the package");
  }
};

const cancelForm = () => {
  router.push("/packages_list");
};
</script>


<style scoped>
.add-package-wrapper {
  background: #fff;
  border-radius: 1.2rem;
  padding: 2rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
  animation: fadeIn 0.6s ease;
}

/* ===== Header ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}
.page-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #111827;
}
.page-subtitle {
  color: #6b7280;
  font-size: 0.9rem;
}
.btn-help {
  background: #111827;
  color: #fff;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-help:hover {
  transform: translateY(-2px);
}

/* ===== Form Card ===== */
.form-card {
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 2rem;
  background: #fafafa;
  transition: box-shadow 0.3s ease;
}
.form-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}
.form-group {
  display: flex;
  flex-direction: column;
}
.form-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}
.form-input,
textarea,
select {
  border: 1px solid #d1d5db;
  border-radius: 0.6rem;
  padding: 0.8rem 1rem;
  font-size: 0.95rem;
  color: #111827;
  background: #fff;
  transition: border 0.3s ease, box-shadow 0.3s ease;
}
.form-input:focus,
textarea:focus,
select:focus {
  border-color: #111827;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.1);
  outline: none;
}
textarea {
  resize: none;
}

/* ===== Form Row ===== */
.form-row {
  display: flex;
  gap: 1rem;
}
.form-row .half {
  flex: 1;
}

/* ===== Footer ===== */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}
.btn-primary {
  background: #111827;
  color: #fff;
  padding: 0.7rem 1.5rem;
  border-radius: 0.6rem;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}
.btn-primary:hover {
  transform: translateY(-1px);
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
  padding: 0.7rem 1.5rem;
  border-radius: 0.6rem;
  border: none;
  cursor: pointer;
  font-weight: 500;
}

/* ===== Animation ===== */
.fade-in {
  animation: fadeIn 0.5s ease forwards;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .btn-help {
    align-self: flex-start;
  }
}

/* === 📱 MOBILE OPTIMIZATION FIXES === */
@media (max-width: 768px) {
  .add-package-wrapper {
    padding: 1.5rem !important;
  }

  /* Header */
  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem;
  }

  .page-title {
    font-size: 1.4rem !important;
  }

  .page-subtitle {
    font-size: 0.85rem !important;
  }

  .header-right {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .btn-help {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
  }

  /* Form */
  .form-card {
    padding: 1.4rem !important;
  }

  .form-content {
    gap: 1rem !important;
  }

  .form-label {
    font-size: 0.9rem !important;
  }

  .form-input,
  textarea,
  select {
    font-size: 0.9rem !important;
    padding: 0.7rem 0.9rem !important;
  }

  .form-row {
    flex-direction: column !important;
    gap: 1rem !important;
  }

  /* Footer Buttons */
  .form-footer {
    flex-direction: column !important;
    gap: 0.8rem !important;
  }

  .btn-primary,
  .btn-secondary {
    width: 100% !important;
    text-align: center;
    font-size: 0.95rem;
  }
}

/* === 📱 Small devices (≤480px) === */
@media (max-width: 480px) {
  .add-package-wrapper {
    padding: 1rem !important;
    border-radius: 0.8rem;
  }

  .page-title {
    font-size: 1.2rem !important;
  }

  .btn-help {
    width: 100%;
    justify-content: center;
  }

  .form-card {
    padding: 1rem !important;
  }

  .form-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-primary,
  .btn-secondary {
    font-size: 0.9rem;
    padding: 0.6rem 1rem;
  }
}
/* === 📱 MOBILE RESPONSIVE PATCH === */
@media (max-width: 768px) {
  .add-package-wrapper {
    padding: 1.5rem !important;
  }

  /* Header adjustments */
  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }
  .page-title {
    font-size: 1.4rem !important;
  }
  .page-subtitle {
    font-size: 0.85rem !important;
  }
  .header-right {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
  .btn-help {
    font-size: 0.85rem !important;
    padding: 0.5rem 1rem !important;
  }

  /* Form Card */
  .form-card {
    padding: 1.4rem !important;
  }
  .form-content {
    gap: 1rem !important;
  }
  .form-row {
    flex-direction: column !important;
    gap: 1rem !important;
  }

  /* Inputs and Labels */
  .form-label {
    font-size: 0.9rem !important;
  }
  .form-input,
  textarea,
  select {
    font-size: 0.9rem !important;
    padding: 0.7rem 0.9rem !important;
  }

  /* Footer Buttons */
  .form-footer {
    flex-direction: column !important;
    gap: 0.8rem !important;
  }
  .btn-primary,
  .btn-secondary {
    width: 100% !important;
    text-align: center;
    font-size: 0.95rem !important;
  }
}

/* === 📱 EXTRA SMALL DEVICES (≤480px) === */
@media (max-width: 480px) {
  .add-package-wrapper {
    padding: 1rem !important;
    border-radius: 0.8rem !important;
  }
  .page-title {
    font-size: 1.2rem !important;
  }
  .btn-help {
    width: 100% !important;
    justify-content: center !important;
  }
  .form-card {
    padding: 1rem !important;
  }
  .btn-primary,
  .btn-secondary {
    font-size: 0.9rem !important;
    padding: 0.6rem 1rem !important;
  }
}
/* ==========================================================
   🎨 ADDITIONAL CSS PATCH — ADD PACKAGE PAGE
   Improves alignment, button consistency & mobile UX
   ========================================================== */

/* === Buttons Enhancement === */
.btn-primary,
.btn-secondary {
  font-family: "Inter", sans-serif;
  letter-spacing: 0.3px;
  transition: all 0.25s ease-in-out;
}

.btn-primary:active,
.btn-secondary:active {
  transform: scale(0.98);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* === Form Focus Glow === */
.form-input:focus,
textarea:focus,
select:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2) !important;
}

/* === Header Responsiveness Fix === */
@media (max-width: 1024px) {
  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.6rem !important;
  }

  .page-title {
    font-size: 1.45rem !important;
  }

  .page-subtitle {
    font-size: 0.9rem !important;
  }
}

/* === Better Form Layout for Medium Screens === */
@media (max-width: 992px) and (min-width: 769px) {
  .form-card {
    padding: 1.8rem !important;
  }

  .form-input,
  textarea,
  select {
    font-size: 0.95rem;
  }

  .btn-primary,
  .btn-secondary {
    padding: 0.65rem 1.4rem;
  }
}

/* === Small Tablets & Mobile (≤768px) === */
@media (max-width: 768px) {
  .add-package-wrapper {
    padding: 1.4rem !important;
  }

  .form-card {
    padding: 1.2rem !important;
  }

  .form-content {
    gap: 0.9rem !important;
  }

  .form-label {
    font-size: 0.9rem !important;
  }

  .btn-help {
    width: auto;
    font-size: 0.85rem;
  }

  .btn-primary,
  .btn-secondary {
    font-size: 0.9rem !important;
    padding: 0.6rem 1.2rem !important;
    width: 100%;
  }

  .form-footer {
    flex-direction: column !important;
    gap: 0.6rem !important;
  }
}

/* === Extra Small Devices (≤480px) === */
@media (max-width: 480px) {
  .add-package-wrapper {
    border-radius: 10px;
    padding: 1rem !important;
  }

  .page-title {
    font-size: 1.2rem !important;
  }

  .page-subtitle {
    font-size: 0.85rem !important;
  }

  .form-input,
  textarea,
  select {
    font-size: 0.85rem !important;
    padding: 0.65rem 0.9rem !important;
  }

  .btn-primary,
  .btn-secondary {
    font-size: 0.85rem !important;
    padding: 0.6rem 1rem !important;
  }

  .form-footer {
    gap: 0.8rem;
  }
}

/* === Animation polish === */
.fade-in {
  animation: fadeInUp 0.5s ease-out both;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Subtle hover effect for form card === */
.form-card:hover {
  transform: translateY(-2px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

</style>
